/// <reference types="vite/client" />
import { useRef, useState, useCallback } from 'react';
import AvatarViewer, { type AvatarViewerRef } from './components/AvatarViewer';
import ChatPanel from './components/ChatPanel';
import VoiceInput from './components/VoiceInput';
import StatusHUD from './components/StatusHUD';
import { useWebSocket } from './hooks/useWebSocket';
import type { Message, SocketEvent } from './types';

// ── Constants ───────────────────────────────────────────────────────────────

// In production (Docker + nginx), nginx proxies /api, /webhook, /ws on the same
// origin → use relative paths. In local dev (Vite proxy or direct), use the env var.
const IS_PROD = import.meta.env.PROD;
const BACKEND_URL: string = IS_PROD
  ? ''  // relative origin — nginx handles the proxy
  : ((import.meta.env.VITE_BACKEND_URL as string | undefined) ?? 'http://localhost:3001');

// WebSocket: in prod connect to the same host over ws(s), in dev use BACKEND_URL
const WS_URL: string = IS_PROD
  ? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`
  : BACKEND_URL.replace(/^https/, 'wss').replace(/^http/, 'ws') + '/ws';

// ── Helpers ─────────────────────────────────────────────────────────────────

let _id = 0;
const nextId = () => String(++_id);

/** Unique session ID per browser tab. Persists across page reloads but
 *  resets when the tab is closed (sessionStorage). */
const SESSION_ID: string = (() => {
  const key = 'rammux_session_id';
  const existing = sessionStorage.getItem(key);
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(key, id);
  return id;
})();

const WELCOME: Message = {
  id: '0',
  text: 'Saludos humano. Soy RAMMUX, el agente de IA de Webcol. ¿En qué te puedo ayudar hoy?',
  isUser: false,
  timestamp: Date.now(),
};

// ── Component ────────────────────────────────────────────────────────────────

export default function App() {
  const avatarRef = useRef<AvatarViewerRef>(null);

  const [messages, setMessages] = useState<Message[]>([WELCOME]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  // ── Add a message to the chat history ─────────────────────────────────
  const addMessage = useCallback((text: string, isUser: boolean) => {
    setMessages((prev) => [
      ...prev,
      { id: nextId(), text, isUser, timestamp: Date.now() },
    ]);
  }, []);

  // ── Handle incoming WebSocket events from RAMMUX backend ───────────────
  const handleSocketEvent = useCallback(
    async (event: SocketEvent) => {
      if (event.type === 'rammux:speak') {
        setIsProcessing(false);
        addMessage(event.text, false);

        if (event.audioBase64) {
          setIsSpeaking(true);
          try {
            await avatarRef.current?.speakFromBase64(event.audioBase64);
          } finally {
            setIsSpeaking(false);
          }
        }
      }
    },
    [addMessage],
  );

  const { status } = useWebSocket(WS_URL, handleSocketEvent);

  // ── Send a text message through the backend → n8n pipeline ────────────
  const handleSend = useCallback(
    async (text: string) => {
      addMessage(text, true);
      setIsProcessing(true);

      try {
        const res = await fetch(`${BACKEND_URL}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: text, sessionId: SESSION_ID }),
        });

        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        // 202 Accepted: RAMMUX response will arrive via WebSocket
      } catch (err) {
        setIsProcessing(false);
        const msg = err instanceof Error ? err.message : 'Error de conexión';
        addMessage(`⚠ ${msg}`, false);
      }
    },
    [addMessage],
  );

  // ── Voice transcription → treat as a sent message ─────────────────────
  const handleTranscription = useCallback(
    (text: string) => void handleSend(text),
    [handleSend],
  );

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div id="app-container">
      {/* 3D avatar canvas */}
      <div id="canvas-container">
        <StatusHUD status={status} isSpeaking={isSpeaking} />
        <AvatarViewer ref={avatarRef} />
      </div>

      {/* Chat interface */}
      <div id="chat-interface">
        <ChatPanel
          messages={messages}
          onSend={handleSend}
          isProcessing={isProcessing}
          inputPrefix={
            <VoiceInput
              backendUrl={BACKEND_URL}
              onTranscription={handleTranscription}
              disabled={isProcessing || isSpeaking}
            />
          }
        />
      </div>
    </div>
  );
}
